import { useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Ban, CheckCircle2, ChevronLeft, Mail, Replace, Save, Trash2 } from "lucide-react";
import {
  EXPENSE_CATEGORIES,
  MAX_COMMENT_LENGTH,
  auDate,
  auDateTime,
  fromYmd,
  type ExpenseCategory,
  type ExpenseInvoiceDetail,
  type ExpenseInvoiceEventType,
} from "@contractorhub/shared";
import { ApiRequestError, apiDownload, apiFetch, apiUpload, errorMessage } from "../../../lib/api";
import { adminExpenseDetailQuery, adminExpensePdfQuery, invalidateExpenseViews, queryKeys } from "../../../lib/queries";
import { pendingReadInterval } from "../../../lib/polling";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
} from "../../../components/ui";
import { useToast } from "../../../components/toast";
import { ListBackLink, ListPager } from "../../../components/list-pager";
import { listSearch } from "../../../lib/list-return";
import { Amount } from "../../../components/invoices/invoice-display";
import { InvoicePdfCard } from "../../../components/invoices/invoice-pdf-card";
import { PdfDropZone } from "../../../components/invoices/pdf-drop-zone";
import { ExtractedFieldsCard } from "../../../components/invoices/extracted-fields-card";
import { ProjectTagField } from "../../../components/expenses/project-tag-field";
import {
  ExpenseChecks,
  ExpenseStatusBadge,
  ExpenseVerdictBadge,
  categoryLabel,
} from "../../../components/expenses/expense-bits";
import {
  SuggestionChip,
  categoryBasis,
  dateBasis,
  projectBasis,
  suggestedCategoryLabel,
  suggestedDateLabel,
  suggestedProjectLabel,
  takenSuggestions,
} from "../../../components/expenses/expense-suggestion";

export const Route = createFileRoute("/_authed/_admin/expenses_/$id")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(adminExpenseDetailQuery(params.id)),
  // A fresh page for each expense: the pager steps between ids on this one
  // route, and its forms are seeded once — kept, they would save one expense's
  // details onto the next.
  remountDeps: ({ params }) => params.id,
  component: ExpensePage,
});

const EVENT_LABEL: Record<ExpenseInvoiceEventType, string> = {
  RECORDED: "Recorded",
  FILE_ATTACHED: "Document attached",
  FILE_REPLACED: "Document replaced",
  EXTRACTION_DONE: "Read by the model",
  EXTRACTION_FAILED: "Could not be read",
  EXTRACTION_RERUN: "Read again",
  FIELDS_CORRECTED: "Fields edited",
  UPDATED: "Details changed",
  VALIDATION_CHANGED: "Checks changed",
  APPROVED: "Approved for payment",
  PAID: "Marked paid",
  VOIDED: "Voided",
};

/**
 * One expense: its document, its details, its fields and the checks over them.
 * Recording it was paying it (EXP-7), so there is no decision to take — the
 * checks advise what to correct, and Void takes it out of every total, keeping
 * the record. Only a void one can then be deleted for good (EXP-12).
 */
function ExpensePage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  // While the document is being read, look for it landing — the fields, the
  // checks and the suggestions all arrive with it. Bounded, as on an invoice.
  const openedAt = useRef<number | null>(null);
  const { data: detail } = useSuspenseQuery({
    ...adminExpenseDetailQuery(id),
    refetchInterval: (query) =>
      pendingReadInterval(query.state.data?.extraction.status === "PENDING", (openedAt.current ??= Date.now())),
    refetchIntervalInBackground: false,
  });
  const [dialog, setDialog] = useState<"void" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const editable = detail.status !== "VOID";

  async function settle(next: ExpenseInvoiceDetail, message?: string) {
    setDialog(null);
    setError(null);
    queryClient.setQueryData(queryKeys.adminExpenseDetail(next.id), next);
    await invalidateExpenseViews(queryClient);
    if (message) toast(message, { tone: "success" });
  }
  async function fail(err: unknown) {
    if (err instanceof ApiRequestError && err.code === "STALE_WRITE") {
      setDialog(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminExpenseDetail(id) });
      toast("This expense was changed by someone else — reloaded.", { tone: "error" });
      return;
    }
    if (dialog) setError(errorMessage(err));
    else toast(errorMessage(err), { tone: "error" });
  }

  const voidIt = useMutation({
    mutationFn: () =>
      apiFetch("POST /api/manage/expenses/{id}/void", { params: { id }, body: { version: detail.version, note: voidReason.trim() } }),
    onSuccess: (next) => settle(next, "Expense voided"),
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => apiFetch("DELETE /api/manage/expenses/{id}", { params: { id }, query: { version: detail.version } }),
    onSuccess: async () => {
      // Off the page before its query goes, so nothing refetches a row that is gone.
      await navigate({ to: "/expenses", search: listSearch("expenses") });
      queryClient.removeQueries({ queryKey: queryKeys.adminExpenseDetail(id) });
      queryClient.removeQueries({ queryKey: ["admin", "expense-pdf", id] });
      await invalidateExpenseViews(queryClient);
      toast("Expense deleted", { tone: "success" });
    },
    onError: fail,
  });
  const attach = useMutation({
    mutationFn: (file: File) => apiUpload("PUT /api/manage/expenses/{id}/file", { params: { id }, fields: { file } }),
    onSuccess: (next) => settle(next, detail.hasDocument ? "Document replaced" : "Document attached"),
    onError: fail,
  });

  // Read the document again — the overlay of typed fields is kept (CINV-7).
  const rerun = useMutation({
    mutationFn: () =>
      apiFetch("POST /api/manage/expenses/{id}/rerun-extraction", { params: { id }, body: { version: detail.version } }),
    onSuccess: async ({ queued, expense }) => {
      // A fresh read, so a fresh polling window for it to land in.
      openedAt.current = Date.now();
      await settle(expense, queued ? "Reading the document again…" : undefined);
      if (!queued) toast("The read could not be queued — enter the fields by hand.", { tone: "error" });
    },
    onError: fail,
  });
  const canRerun =
    editable &&
    detail.hasDocument &&
    detail.extraction.status !== "PENDING" &&
    detail.extraction.status !== "NOT_CONFIGURED" &&
    detail.extraction.status !== "NO_DOCUMENT";
  // A wrong photo or the wrong PDF used to mean voiding and recording it again.
  const [replacing, setReplacing] = useState(false);

  const busy = voidIt.isPending || remove.isPending;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <ListBackLink list="expenses" className="inline-flex items-center gap-1 text-sm text-muted hover:text-text">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Expenses
        </ListBackLink>
        <ListPager list="expenses" id={detail.id} />
      </div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-text">{detail.supplierName ?? "Vendor not read yet"}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            <ExpenseStatusBadge status={detail.status} />
            {detail.status !== "VOID" && (
              <ExpenseVerdictBadge status={detail.validationStatus} extractionStatus={detail.extractionStatus} />
            )}
            <span>
              {detail.invoiceNumber ?? "No number"} · {auDate(fromYmd(detail.expenseDate))} ·{" "}
              {detail.projectName ?? "No project"} · <Amount value={detail.total} currency={detail.currency} />
            </span>
            {detail.source === "EMAIL" && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                Emailed{detail.recorderName ? ` by ${detail.recorderName}` : ""}
              </span>
            )}
          </div>
        </div>
        {detail.status !== "VOID" ? (
          <Button variant="secondary" icon={Ban} disabled={busy} onClick={() => setDialog("void")}>
            Void
          </Button>
        ) : (
          <Button variant="secondary" icon={Trash2} disabled={busy} onClick={() => setDialog("delete")}>
            Delete permanently
          </Button>
        )}
      </div>
      {/* Only an expense marked paid before recording became paying carries these. */}
      {detail.status === "PAID" && (detail.paymentReference || detail.payerName) && (
        <Alert tone="ok" className="mb-4">
          Marked paid {detail.paidAt ? auDate(fromYmd(detail.paidAt)) : ""}
          {detail.paymentReference ? ` · ref ${detail.paymentReference}` : ""}
          {detail.payerName ? ` · by ${detail.payerName}` : ""}.
        </Alert>
      )}
      {detail.status === "VOID" && (
        <Alert tone="warn" className="mb-4">
          Void: {detail.voidReason}. Kept for the record; counted in no total.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          {detail.hasDocument && detail.fileName ? (
            <div className="space-y-3">
              <InvoicePdfCard
                query={adminExpensePdfQuery(id, detail.fileVersion)}
                fileName={detail.fileName}
                fileVersion={detail.fileVersion}
                contentType={detail.fileContentType}
                onDownload={() => apiDownload("GET /api/manage/expenses/{id}/file", { params: { id } })}
              />
              {editable &&
                (replacing ? (
                  <Card className="p-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="text-sm text-muted">
                        The new document is read afresh; what you typed over the fields is kept. The old one stays in the history.
                      </p>
                      <Button variant="ghost" size="sm" onClick={() => setReplacing(false)} disabled={attach.isPending}>
                        Cancel
                      </Button>
                    </div>
                    <PdfDropZone
                      kind="expense"
                      file={null}
                      disabled={attach.isPending}
                      onFile={(file, problem) => {
                        if (file && !problem)
                          attach.mutate(file, { onSuccess: () => setReplacing(false) });
                        else if (problem) toast(problem, { tone: "error" });
                      }}
                    />
                  </Card>
                ) : (
                  <Button variant="secondary" size="sm" icon={Replace} onClick={() => setReplacing(true)}>
                    Replace document
                  </Button>
                ))}
            </div>
          ) : (
            <Card>
              <CardHeader title="Document" description="Entered by hand. Attach the invoice or receipt to keep it with the record." />
              <div className="px-5 pb-5">
                {editable ? (
                  <PdfDropZone
                    kind="expense"
                    file={null}
                    disabled={attach.isPending}
                    onFile={(file, problem) => {
                      if (file && !problem) attach.mutate(file);
                      else if (problem) toast(problem, { tone: "error" });
                    }}
                  />
                ) : (
                  <p className="text-sm text-subtle">No document was attached.</p>
                )}
              </div>
            </Card>
          )}
          <ExpenseAttributes detail={detail} onSaved={(next) => void settle(next, "Details saved")} />
        </div>

        <div className="space-y-6">
          <ExtractedFieldsCard
            detail={detail}
            editable={editable}
            canRerun={canRerun}
            rerunPending={rerun.isPending}
            onRerun={() => rerun.mutate()}
            onSaved={(next) => void settle(next)}
            save={(body) => apiFetch("PATCH /api/manage/expenses/{id}/fields", { params: { id }, body })}
            detailQueryKey={queryKeys.adminExpenseDetail(id)}
            noun="expense"
          />
          <Card>
            <CardHeader
              title="Checks"
              description={detail.validation?.summary ?? "The checks run once the document has been read."}
            />
            {detail.validation && <ExpenseChecks checks={detail.validation.checks} />}
          </Card>
          <Card>
            <CardHeader title="History" />
            <ul className="divide-y divide-line">
              {[...detail.events].reverse().map((e) => (
                <li key={e.id} className="flex justify-between gap-3 px-5 py-2 text-sm">
                  <span className="text-text">
                    {EVENT_LABEL[e.type]}
                    {e.note ? <span className="text-subtle"> — {e.note}</span> : null}
                  </span>
                  <span className="shrink-0 text-xs text-subtle">
                    {e.actorName ?? "System"} · {auDateTime(new Date(e.createdAt))}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <Modal
        open={dialog === "void"}
        onClose={() => setDialog(null)}
        size="sm"
        title="Void this expense"
        description="It stays on record, out of every total. This cannot be undone."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)} disabled={voidIt.isPending}>
              Cancel
            </Button>
            <Button variant="danger" icon={Ban} loading={voidIt.isPending} disabled={voidReason.trim().length < 3} onClick={() => voidIt.mutate()}>
              Void
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label="Why" required>
            <Textarea
              rows={3}
              value={voidReason}
              maxLength={MAX_COMMENT_LENGTH}
              placeholder="e.g. Recorded twice."
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={dialog === "delete"}
        onClose={() => setDialog(null)}
        size="sm"
        title="Delete this expense permanently"
        description="Its details, its document and its history are removed for good. This cannot be undone."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="danger" icon={Trash2} loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete permanently
            </Button>
          </>
        }
      >
        {error && <Alert tone="error">{error}</Alert>}
      </Modal>
    </div>
  );
}

/** The expense's own details — which project it is for can be corrected even after approval. */
function ExpenseAttributes({ detail, onSaved }: { detail: ExpenseInvoiceDetail; onSaved: (next: ExpenseInvoiceDetail) => void }) {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState(detail.projectId);
  const [category, setCategory] = useState<ExpenseCategory | "">(detail.category ?? "");
  const [expenseDate, setExpenseDate] = useState(detail.expenseDate);
  const [note, setNote] = useState(detail.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const editable = detail.status !== "VOID";
  const dirty =
    projectId !== detail.projectId ||
    (category || null) !== detail.category ||
    expenseDate !== detail.expenseDate ||
    (note.trim() || null) !== detail.note;
  // An upload nobody has confirmed: Save confirms it even unchanged, and the
  // suggestions offer what to change it to (EXP-8).
  const unconfirmed = detail.status !== "VOID" && !detail.detailsConfirmed;
  const s = detail.suggestion;

  const save = useMutation({
    mutationFn: () => {
      // Only what changed: any of the three confirms an upload (EXP-8), and a
      // note alone must not. "Confirm details" — nothing changed — sends all three.
      const confirming = !dirty;
      const details = {
        ...(confirming || projectId !== detail.projectId ? { projectId } : {}),
        ...(confirming || (category || null) !== detail.category ? { category: category || null } : {}),
        ...(confirming || expenseDate !== detail.expenseDate ? { expenseDate } : {}),
      };
      const taken = takenSuggestions(s, { projectId, category: category || null, expenseDate });
      return apiFetch("PATCH /api/manage/expenses/{id}", {
        params: { id: detail.id },
        body: {
          version: detail.version,
          ...details,
          ...((note.trim() || null) !== detail.note ? { note: note.trim() || null } : {}),
          fromSuggestion: taken.filter((field) => field in details),
        },
      });
    },
    onSuccess: (next) => {
      setError(null);
      onSaved(next);
    },
    onError: async (err) => {
      if (err instanceof ApiRequestError && err.code === "STALE_WRITE") {
        await queryClient.invalidateQueries({ queryKey: queryKeys.adminExpenseDetail(detail.id) });
      }
      setError(errorMessage(err));
    },
  });

  return (
    <Card>
      <CardHeader
        title="Details"
        description={
          unconfirmed
            ? "Uploaded without these. Check what's suggested, then confirm."
            : "What the expense is for, and when it counts."
        }
        actions={
          editable && (dirty || unconfirmed) ? (
            <Button size="sm" icon={dirty ? Save : CheckCircle2} loading={save.isPending} onClick={() => save.mutate()}>
              {dirty ? "Save" : "Confirm details"}
            </Button>
          ) : null
        }
      />
      <div className="grid gap-4 px-5 pb-5 sm:grid-cols-2">
        {error && (
          <div className="sm:col-span-2">
            <Alert tone="error">{error}</Alert>
          </div>
        )}
        <div className="sm:col-span-2">
          {editable ? (
            <>
              <ProjectTagField value={projectId} onChange={setProjectId} />
              {s?.project && s.project.projectId !== projectId && (
                <SuggestionChip
                  value={suggestedProjectLabel(detail)!}
                  basis={projectBasis(detail)}
                  onApply={() => setProjectId(s.project!.projectId)}
                />
              )}
            </>
          ) : (
            <Field label="Project">
              <p className="text-sm text-text">{detail.projectName ?? "No project"}</p>
            </Field>
          )}
        </div>
        <div>
          <Field label="Category">
            <Select value={category} disabled={!editable} onChange={(e) => setCategory(e.target.value as ExpenseCategory | "")}>
              <option value="">Uncategorised</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </Select>
          </Field>
          {editable && s?.category && s.category.value !== (category || null) && (
            <SuggestionChip
              value={suggestedCategoryLabel(detail)!}
              basis={categoryBasis(detail)}
              onApply={() => setCategory(s.category!.value)}
            />
          )}
        </div>
        <div>
          <Field label="Expense date">
            <Input type="date" value={expenseDate} disabled={!editable} onChange={(e) => setExpenseDate(e.target.value)} />
          </Field>
          {editable && s?.expenseDate && s.expenseDate.value !== expenseDate && (
            <SuggestionChip
              value={suggestedDateLabel(detail)!}
              basis={dateBasis(detail)}
              onApply={() => setExpenseDate(s.expenseDate!.value)}
            />
          )}
        </div>
        <div className="sm:col-span-2">
          <Field label="Note">
            <Input value={note} disabled={!editable} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </div>
    </Card>
  );
}
